'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Badge, type BadgeProps } from '@/ui/badge/Badge'
import { CloseButton } from '@/ui/buttons/close-button/CloseButton.client'
import { Link } from '@/ui/links/link/Link.client'
import type { LinkButtonProps } from '@/ui/links/link/Link.types'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { cva } from 'class-variance-authority'
import { useId, type CSSProperties } from 'react'

const bannerRoot = cva(
  'flex flex-col items-stretch gap-x-2 gap-y-2 rounded-sm bg-white p-2 tablet:flex-row tablet:items-center',
)

export type BannerHeading = {
  title?: string
  titleColor?: string
  subtitle?: string
  subtitleColor?: string
}

export type BannerBadgeConfig = {
  text: string
  /**
   * Badge tint (`Badge` prop).
   * Use `none` with `style` for full BO styling (colors, background, etc.).
   * Defaults to `Badge` default tint (`default`).
   */
  tint?: BadgeProps['tint']
  /** Badge size. Defaults to `md`. */
  size?: BadgeProps['size']
  /** Inline styles from BO; typically paired with `tint: 'none'`. */
  style?: CSSProperties
}

/** Same API as `Link` `text-link`, with optional `style` for BO color overrides. */
export type BannerActionConfig = StrictOmit<LinkButtonProps, 'variant'> & {
  style?: CSSProperties
}

export type BannerProps = UiComponentProps<{
  /** Title and optional subtitle. */
  heading?: BannerHeading
  /** Optional badge; `tint` defaults to `default`, `size` to `md`. Use `tint: 'none'` + `style` for BO colors. */
  badge?: BannerBadgeConfig
  /** Optional primary action (`Link` `text-link`, underlined by default). */
  action?: BannerActionConfig
  /**
   * Optional background color from BO (arbitrary hex).
   * Applied as inline style because dynamic values cannot be expressed as Tailwind classes.
   */
  backgroundColor?: string
  /** When set, shows the dismiss control and calls this handler on press. */
  onClose?: () => void
  /** Overrides the default translated label for the close control. */
  closeButtonAriaLabel?: string
}>

type BannerHeadingBlockProps = BannerHeading & {
  titleId: string
  'data-testid': string
}

function BannerHeadingBlock({
  titleId,
  'data-testid': dataTestId,
  title,
  titleColor,
  subtitle,
  subtitleColor,
}: BannerHeadingBlockProps) {
  if (!title && !subtitle) {
    return null
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {title && (
        <p
          id={titleId}
          className={twMerge(
            'font-bold leading-light',
            titleColor === undefined && 'text-brand-2-900',
          )}
          style={titleColor !== undefined ? { color: titleColor } : undefined}
          data-testid={`${dataTestId}/title`}
        >
          {title}
        </p>
      )}
      {subtitle && (
        <p
          className={twMerge(
            'text-sm leading-light',
            subtitleColor === undefined && 'text-brand-2-900',
          )}
          style={subtitleColor !== undefined ? { color: subtitleColor } : undefined}
          data-testid={`${dataTestId}/subtitle`}
        >
          {subtitle}
        </p>
      )}
    </div>
  )
}

export function Banner({
  heading,
  badge,
  action,
  backgroundColor,
  onClose,
  closeButtonAriaLabel,
  id,
  className,
  'data-testid': dataTestId = 'banner',
  'aria-label': ariaLabel,
}: BannerProps) {
  const { t } = useTranslation(['banner'])
  const reactId = useId()
  const titleId = id ? `${id}-title` : `banner-title-${reactId}`
  const labelledBy = !ariaLabel && heading?.title ? titleId : undefined
  const closeLabel = closeButtonAriaLabel ?? t('banner:close')

  return (
    <section
      id={id}
      className={twMerge(className, bannerRoot())}
      style={backgroundColor ? { backgroundColor } : undefined}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2 tablet:flex-row tablet:items-center">
        {(badge || onClose) && (
          <div className="flex items-center justify-between gap-x-2 tablet:contents">
            {badge && (
              <Badge
                tint={badge.tint ?? 'default'}
                size={badge.size ?? 'md'}
                style={badge.style}
                className="shrink-0 self-start tablet:self-center"
                data-testid={`${dataTestId}/badge`}
              >
                {badge.text}
              </Badge>
            )}
            {onClose && (
              <div className="ml-auto shrink-0 tablet:hidden">
                <CloseButton
                  variant="quaternary"
                  onPress={onClose}
                  data-testid={`${dataTestId}/close`}
                >
                  {closeLabel}
                </CloseButton>
              </div>
            )}
          </div>
        )}

        <BannerHeadingBlock
          titleId={titleId}
          data-testid={dataTestId}
          title={heading?.title}
          titleColor={heading?.titleColor}
          subtitle={heading?.subtitle}
          subtitleColor={heading?.subtitleColor}
        />

        {(action || onClose) && (
          <div
            className={twMerge(
              'flex shrink-0 flex-row flex-nowrap items-center gap-x-2 tablet:ml-auto',
              !action && onClose && 'hidden tablet:flex',
            )}
          >
            {action && (
              <Link
                {...action}
                variant="text-link"
                bold={action.bold ?? true}
                className={twMerge('whitespace-nowrap', action.className)}
                data-testid={action['data-testid'] ?? `${dataTestId}/action`}
              />
            )}
            {onClose && (
              <div className="hidden shrink-0 tablet:block">
                <CloseButton variant="quaternary" onPress={onClose}>
                  {closeLabel}
                </CloseButton>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export function BannerSkeleton({ className }: { className?: string }) {
  return (
    <div className={twMerge(className, bannerRoot())} aria-hidden data-testid="banner-skeleton">
      <div className="flex min-w-0 flex-1 flex-col gap-2 tablet:flex-row tablet:items-center">
        <div className="flex items-center justify-between gap-x-2 tablet:contents">
          <div className="shrink-0 self-start tablet:self-center">
            <BaseSkeleton className="h-5 w-15 rounded-full" />
          </div>
          <div className="tablet:hidden">
            <BaseSkeleton className="size-4 shrink-0 rounded-full" />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-y-1">
          <BaseSkeleton className="h-[.875rem] w-18 max-w-[80%]" />
          <BaseSkeleton className="h-[.875rem] w-full max-w-xl" />
        </div>
        <div className="flex flex-row flex-nowrap items-center gap-x-2 tablet:ml-auto">
          <BaseSkeleton className="h-[.875rem] w-[5.625rem] shrink-0 rounded-full" />
          <div className="hidden tablet:block">
            <BaseSkeleton className="size-4 shrink-0 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  )
}
